import React, { useState } from 'react'
import { Form, Button, Row, Col, InputGroup, Spinner } from 'react-bootstrap'
import useCreatePhysicalPhone, {
  CreatePhysicalPhoneParams,
} from 'hooks/use-create-physical-phone'
import { useNextDeviceNames } from 'hooks/use-next-device-names'
import toast from 'react-hot-toast'

interface AddPhysicalPhoneFormProps {
  terminalId: string
  terminalName: string
  onSuccess?: () => void
}

export const AddPhysicalPhoneForm: React.FC<AddPhysicalPhoneFormProps> = ({
  terminalId,
  terminalName,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    deviceModel: 'iPhone15Pro',
    serialNumber: '',
    imei: '',
    count: 1,
  })

  const createPhysicalPhone = useCreatePhysicalPhone()

  const { data: namesData, isLoading: isLoadingNames } = useNextDeviceNames({
    terminalId,
    terminalName,
    deviceType: 'physical-phone',
    count: formData.count,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!namesData?.names) {
      toast.error('Device names not generated yet')
      return
    }

    if (!formData.serialNumber || !formData.imei) {
      toast.error('Please fill in all required fields')
      return
    }

    // Validate IMEI format (exactly 15 digits)
    if (
      formData.imei &&
      (formData.imei.length !== 15 || !/^\d{15}$/.test(formData.imei))
    ) {
      toast.error('IMEI must be exactly 15 digits')
      return
    }

    try {
      const createParams: CreatePhysicalPhoneParams = {
        terminalId,
        deviceModel: formData.deviceModel,
        serialNumber: formData.serialNumber,
        imei: formData.imei,
        names: namesData.names,
      }

      await createPhysicalPhone.mutateAsync(createParams)

      // Reset form
      setFormData({
        deviceModel: 'iPhone15Pro',
        serialNumber: '',
        imei: '',
        count: 1,
      })

      onSuccess?.()
    } catch (error) {
      // Error handling is done in the hook
    }
  }

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Device Model *</Form.Label>
            <Form.Control
              as="select"
              value={formData.deviceModel}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleInputChange('deviceModel', e.target.value)
              }
              required
            >
              <option value="iPhone14">iPhone 14</option>
              <option value="iPhone14Pro">iPhone 14 Pro</option>
              <option value="iPhone15">iPhone 15</option>
              <option value="iPhone15Pro">iPhone 15 Pro</option>
              <option value="iPhone16">iPhone 16</option>
              <option value="iPhone16Pro">iPhone 16 Pro</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Count</Form.Label>
            <Form.Control
              type="number"
              value={1}
              disabled
              readOnly
              className="bg-secondary text-white"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Serial Number *</Form.Label>
            <Form.Control
              type="text"
              value={formData.serialNumber}
              onChange={(e) =>
                handleInputChange('serialNumber', e.target.value)
              }
              placeholder="IP-XXXX-2024"
              required
            />
            <Form.Text className="text-muted">
              Serial number for the Physical Phone
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>IMEI *</Form.Label>
            <Form.Control
              type="text"
              value={formData.imei}
              onChange={(e) => handleInputChange('imei', e.target.value)}
              placeholder="123456789012345"
              minLength={15}
              maxLength={15}
              required
            />
            <Form.Text className="text-muted">
              IMEI must be exactly 15 digits (e.g., 123456789012345)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Generated Names Preview</Form.Label>
            <InputGroup>
              <Form.Control
                value={
                  isLoadingNames
                    ? 'Loading...'
                    : namesData?.names.join(', ') || ''
                }
                readOnly
                className="bg-light"
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Names will be auto-generated based on current received devices
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2">
        <Button
          type="submit"
          variant="success"
          disabled={createPhysicalPhone.isLoading || isLoadingNames}
        >
          {createPhysicalPhone.isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            `Create Physical Phone`
          )}
        </Button>
      </div>
    </Form>
  )
}
